import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Square, ArrowRight, Check, Sparkles, Eye, Shield, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const HERO_IMAGE = "https://images.unsplash.com/photo-1761641428582-b85a085340d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHx3aW5kb3clMjBjbGVhbmluZyUyMHByb2Zlc3Npb25hbCUyMGdsYXNzfGVufDB8fHx8MTc3NDExODc1Nnww&ixlib=rb-4.1.0&q=85";
const CONTENT_IMAGE_1 = "https://images.unsplash.com/photo-1769788161278-8dc624a2d537?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHx3aW5kb3clMjBjbGVhbmluZyUyMHByb2Zlc3Npb25hbCUyMGdsYXNzfGVufDB8fHx8MTc3NDExODc1Nnww&ixlib=rb-4.1.0&q=85";

export const NettoyageVitresPage = () => {
  const questions = [
    { id: 'nb_vitres', label: 'Nombre de vitres approximatif ?', type: 'select', options: ['1-5', '6-10', '11-20', 'Plus de 20'] },
    { id: 'type_vitres', label: 'Type de vitres ?', type: 'select', options: ['Standard', 'Baies vitrées', 'Véranda', 'Fenêtres de toit', 'Mixte'] },
    { id: 'hauteur', label: 'Accessibilité ?', type: 'select', options: ['Rez-de-chaussée', '1-2 étages', '3+ étages', 'Difficile d\'accès'] },
    { id: 'frequence', label: 'Fréquence souhaitée ?', type: 'select', options: ['Ponctuel', 'Trimestriel', 'Semestriel', 'Annuel'] }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* PILIER 1: ATTENTION */}
      <section className="hero-section relative min-h-[70vh] flex items-center">
        <img src={HERO_IMAGE} alt="Nettoyage vitres professionnel" className="hero-image-bg" />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Square className="h-10 w-10 text-white" />
              <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">Homely Nettoyage</Badge>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              Des vitres si propres qu'elles semblent invisibles
            </h1>
            
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl">
              Nettoyage de vitres professionnel sans traces. Intérieur, extérieur, 
              hauteur : nous intervenons partout avec le matériel adapté.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Sparkles className="h-5 w-5 text-yellow-400" />
                <span className="text-white text-sm">Zéro trace garantie</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Eye className="h-5 w-5 text-blue-400" />
                <span className="text-white text-sm">Transparence parfaite</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Shield className="h-5 w-5 text-green-400" />
                <span className="text-white text-sm">Assurance RC</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="btn-premium h-14 px-10 text-base rounded-full"
                onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
              >
                Devis gratuit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2 text-white">
                <span className="text-2xl font-bold">Dès 5 CHF/vitre</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILIER 2: ÉMOTION */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img src={CONTENT_IMAGE_1} alt="Laveur de vitres professionnel" className="rounded-2xl shadow-2xl w-full" />
            </div>

            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Pourquoi vos vitres ont-elles toujours des traces ?
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                Le calcaire de l'eau, les produits ménagers inadaptés et les chiffons 
                qui laissent des fibres... Nettoyer ses vitres sans traces est un vrai défi 
                sans les bons outils professionnels.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { problem: 'Eau du robinet', solution: 'Nous utilisons de l\'eau déminéralisée pure' },
                  { problem: 'Produits grand public', solution: 'Solutions professionnelles anti-traces' },
                  { problem: 'Mauvais outils', solution: 'Raclettes, perches et mouilleurs pro' },
                  { problem: 'Technique inadaptée', solution: '10+ ans d\'expérience et formation' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-secondary/30 rounded-lg">
                    <span className="text-red-500 line-through text-sm w-40">{item.problem}</span>
                    <ArrowRight className="h-4 w-4 text-[var(--sage)]" />
                    <span className="text-sm font-medium" style={{ color: 'var(--sage-dark)' }}>{item.solution}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILIER 3: MÉMOIRE - Services */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Nos prestations</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Tous types de vitrages, toutes hauteurs
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              'Fenêtres standard',
              'Baies vitrées',
              'Vérandas',
              'Fenêtres de toit (Velux)',
              'Garde-corps en verre',
              'Vitrines commerciales'
            ].map((item, idx) => (
              <div key={idx} className="card-premium p-6 text-center">
                <Check className="h-8 w-8 mx-auto mb-3 text-[var(--sage)]" />
                <h3 className="font-semibold">{item}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILIER 4: DÉCISION */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-2xl mb-12" style={{ backgroundColor: 'var(--sage)' }}>
            {[
              { value: '2\'000+', label: 'Clients satisfaits' },
              { value: '0 trace', label: 'Garantie résultat' },
              { value: '48h', label: 'Délai intervention' },
              { value: '4.9/5', label: 'Note Google' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <p className="text-3xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="bg-secondary/30 p-8 rounded-2xl max-w-3xl mx-auto text-center">
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />)}
            </div>
            <p className="text-lg italic mb-4">
              "Enfin des vitres parfaitement propres ! J'ai essayé pendant des années 
              sans jamais y arriver. Homely l'a fait en 1h."
            </p>
            <p className="font-semibold">— Anne-Marie G., Nyon</p>
          </div>
        </div>
      </section>

      {/* PILIER 5: ACTION */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} id="form">
        <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Demandez votre devis gratuit
            </h2>
            <p className="text-white/70">Estimation précise basée sur le nombre de vitres</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <ServiceLeadForm service="nettoyage" pole="nettoyage" questions={questions} />
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h3 className="font-semibold mb-4">Autres services de nettoyage</h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/nettoyage/canape" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage canapé</Link>
            <Link to="/nettoyage/terrasse" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage terrasse</Link>
            <Link to="/nettoyage/fin-de-chantier" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Fin de chantier</Link>
            <Link to="/nettoyage/apres-bail" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Après bail</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
